import React, { useCallback, useMemo, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { partition } from 'lodash-es';

import SimpleDropdown from '../Dropdowns/SimpleDropdown';
import { NavigationTabsWithMore } from '../NavigationTabs';

import { getCurrentTab, getPageOptions, getPeriodFilters, permissionsMapped } from './config';
import { appStore, isUnified } from '../../utils/helpers';

const pageLinks = (currentTab, { store, ref_no }) => {
  const periodFilters = getPeriodFilters();

  switch (currentTab) {
    case 'insights':
      return `/apps/${store}/${ref_no}/overview`;
    case 'performance':
      return `/apps/${store}/${ref_no}/performance${periodFilters}`;
    case 'downloads_and_revenue':
      return `/apps/${store}/${ref_no}/downloads_and_revenue${periodFilters}`;
    case 'rank_history':
      return `/apps/${store}/${ref_no}/rank_history${periodFilters}`;
    case 'engagement':
      return `/apps/${store}/${ref_no}/engagement${periodFilters}`;
    case 'user_retention':
      return `/apps/${store}/${ref_no}/user_retention`;
    case 'audience':
      return `/apps/${store}/${ref_no}/user_graph`;
    case 'review_text':
      return `/review-analysis/sentiments/${store}/${ref_no}${periodFilters}`;
    case 'ad_creatives':
      return `/ad-intelligence/advertising-overview/${store}/${ref_no}${periodFilters}`;
    case 'sdks':
      return `/apps/${store}/${ref_no}/sdks`;
    case 'search_text':
      return `/search-analysis/organic_keywords/${store}/${ref_no}`;
    case 'app_analysis':
      return `/x-ray/app_analysis/${store}/${ref_no}`;
    default:
      return `/apps/${store}/${ref_no}/overview`;
  }
};

const normalizedCurrentTab = (currentTab) => {
  switch (currentTab) {
    case 'sentiments':
    case 'intents':
    case 'keywords':
      return 'review_text';
    case 'user_graph':
    case 'demographics':
      return 'audience';
    case 'advertising-overview':
      return 'ad_creatives';
    case 'organic_keywords':
    case 'paid_searches':
      return 'search_text';
    default:
      return currentTab;
  }
};

const pagesWithUnifiedSupport = [
  'insights',
  'performance',
  'downloads_and_revenue',
  'engagement',
  'review_text',
  'ad_creatives',
];

const AppInsightsNavigation = ({
  currentTab,
  store,
  appRefNos,
  allowedPages,
  unselectedStateText,
  variant = 'dropdown',
  className = 'redesign',
}) => {
  const pageOptions = useMemo(() => getPageOptions(allowedPages), [allowedPages]);

  const handlePageChange = useCallback((selectedPage) => {
    const withUnifiedSupport = pagesWithUnifiedSupport.includes(selectedPage);
    const normalizedStore = (isUnified(store) && !withUnifiedSupport)
      ? 'itunes_connect'
      : store;

    const link = pageLinks(selectedPage, {
      store: normalizedStore,
      ref_no: appRefNos[appStore(normalizedStore, { unifiedKey: 'both' })],
    });

    window.location.assign(link);
  }, [store, appRefNos]);

  if (variant === 'tabs') {
    // Prepare the options for the menu
    const labelsMap = {
      insights: 'Overview',
      performance: 'Performance',
      search_text: 'Search Analysis',
      app_analysis: 'Features',
    };
    const moreOptionKeys = ['user_retention', 'app_analysis', 'search_text', 'audience'];

    const decoratedPageOptions = pageOptions.map(option => ({
      ...option,
      label: labelsMap[option.value] || option.label,
    }));

    const [
      tabOptions,
      moreOptions,
    ] = partition(decoratedPageOptions, option => !moreOptionKeys.includes(option.value));

    const orderedMoreOptions = moreOptionKeys.reduce((acc, item) => {
      const option = moreOptions.find(({ value }) => value === item);
      return option ? [...acc, option] : acc;
    }, []);

    const orderedPageOptions = [...tabOptions, ...orderedMoreOptions];
    const splitIndex = orderedPageOptions.findIndex(({ value }) => moreOptionKeys.includes(value));

    return (
      <NavigationTabsWithMore
        active={normalizedCurrentTab(currentTab)}
        options={orderedPageOptions}
        tabLimit={splitIndex !== -1 ? splitIndex : orderedPageOptions.length}
        onChange={handlePageChange}
      />
    );
  }

  return (
    <div className={cx('app-pages-dropdown', className)}>
      <SimpleDropdown
        selected={normalizedCurrentTab(currentTab)}
        options={pageOptions}
        size="lg"
        variant="light"
        className="pages-dropdown"
        unselectedStateText={unselectedStateText}
        onChange={handlePageChange}
      />
    </div>
  );
};

AppInsightsNavigation.propTypes = {
  allowedPages: T.object,
  currentTab: T.string,
  store: T.string,
  appRefNos: T.shape({
    itunes_connect: T.string,
    google_play: T.string,
    both: T.string,
  }),
  unselectedStateText: T.string,
  variant: T.string,
  className: T.string,
};

export default AppInsightsNavigation;

export const AppInsightsNavigationConnector = ({
  nodeId = 'app-insights-general-root',
  ...AppInsightsNavigationProps
}) => {
  const { current: rootNode } = useRef(document.getElementById(nodeId));
  const { current: currentTab } = useRef(getCurrentTab(window.location.pathname));

  if (!rootNode) return null;

  const allowedPages = permissionsMapped.reduce((memo, { data_name, tab }) => ({
    ...memo,
    [tab]: JSON.parse(rootNode.dataset?.allowedPages)?.[data_name] || false,
  }), {});

  const appRefNos = JSON.parse(rootNode.dataset?.appRefs || '{}');

  return (
    <AppInsightsNavigation
      allowedPages={allowedPages}
      currentTab={currentTab}
      appRefNos={appRefNos}
      {...AppInsightsNavigationProps}
    />
  );
};

AppInsightsNavigationConnector.propTypes = {
  nodeId: T.string,
};
